import React from 'react';
import { Link } from 'react-router-dom';

// Landmark peaks across all 13 Indian Himalayan States
// Positioned left (west) to right (east) geographically
const PEAKS = [
  { name: 'Nun Kun', alt: '7,135m', x: 95, y: 72, state: 'J&K' },
  { name: 'Stok Kangri', alt: '6,153m', x: 165, y: 88, state: 'Ladakh' },
  { name: 'Deo Tibba', alt: '6,001m', x: 310, y: 82, state: 'Himachal' },
  { name: 'Hanuman Tibba', alt: '5,860m', x: 390, y: 95, state: 'Himachal' },
  { name: 'Nanda Devi', alt: '7,816m', x: 560, y: 42, state: 'Uttarakhand', major: true },
  { name: 'Trishul', alt: '7,120m', x: 630, y: 62, state: 'Uttarakhand' },
  { name: 'Sandakphu', alt: '3,636m', x: 780, y: 108, state: 'W. Bengal' },
  { name: 'Kangchenjunga', alt: '8,586m', x: 850, y: 28, state: 'Sikkim', major: true },
  { name: 'Gorichen', alt: '6,858m', x: 1000, y: 55, state: 'Arunachal' },
  { name: 'Saramati', alt: '3,841m', x: 1100, y: 92, state: 'Nagaland' },
  { name: 'Mt. Iso', alt: '2,994m', x: 1160, y: 110, state: 'Manipur' },
  { name: 'Shillong Peak', alt: '1,965m', x: 1040, y: 125, state: 'Meghalaya' },
  { name: 'Phawngpui', alt: '2,157m', x: 1210, y: 118, state: 'Mizoram' },
];

export default function HimalayanPeaksFooter({ accentColor = '#C19A6B' }) {
  return (
    <section className="relative pt-10 pb-0 overflow-hidden" data-testid="himalayan-peaks-footer">
      <div className="text-center mb-6 px-6">
        <p className="text-white/25 text-[9px] tracking-[0.5em] uppercase">
          The Himalayan Arc &middot; 13 States &middot; 2,500 km
        </p>
      </div>
      <div className="relative w-full">
        <svg viewBox="0 0 1440 320" className="w-full block" preserveAspectRatio="none" style={{ minHeight: '220px' }}>
          <defs>
            {/* Distant range fill */}
            <linearGradient id="distantRange" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.1)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.03)" />
            </linearGradient>
            {/* Main range fill */}
            <linearGradient id="mainRange" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.04)" />
            </linearGradient>
            {/* Foothills */}
            <linearGradient id="foothills" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0.15" />
              <stop offset="100%" stopColor={accentColor} stopOpacity="0.03" />
            </linearGradient>
            {/* Snow cap */}
            <linearGradient id="snowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.15)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
          </defs>

          {/* Layer 1: Far distant range — very subtle */}
          <path
            d="M0 320 L0 220 L30 210 L70 218 L110 195 L150 205 L190 180 L230 190 L270 170 L310 178 L350 155 L390 165 L430 145 L470 155 L510 130 L550 142 L590 120 L620 130 L650 108 L680 118 L710 100 L740 112 L770 95 L800 105 L830 85 L860 98 L890 80 L920 92 L950 85 L980 98 L1010 105 L1050 115 L1090 125 L1130 135 L1170 140 L1210 145 L1250 155 L1290 165 L1330 175 L1370 185 L1410 192 L1440 200 L1440 320 Z"
            fill="url(#distantRange)"
          />

          {/* Layer 2: Main peaks — the dramatic range */}
          <path
            d="M0 320 L0 240 L25 235 L55 242 L85 225 L110 232 L140 210 L165 218 L195 195 L220 205 L250 185 L275 192 L305 170 L330 180 L355 158 L380 168 L405 148 L425 156 L450 135 L470 145 L495 122 L515 132 L540 110 L555 118 L575 95 L590 108 L610 88 L625 98 L645 78 L660 90 L680 68 L695 80 L715 60 L730 72 L750 52 L765 65 L785 48 L800 60 L820 50 L835 62 L855 45 L870 58 L895 50 L915 62 L940 55 L960 68 L985 60 L1010 72 L1035 80 L1060 90 L1090 100 L1120 108 L1150 115 L1180 122 L1210 128 L1240 140 L1270 152 L1300 162 L1330 172 L1360 180 L1390 190 L1420 200 L1440 210 L1440 320 Z"
            fill="url(#mainRange)"
          />

          {/* Snow caps on major peaks */}
          {/* Nanda Devi region */}
          <path d="M540 110 L548 104 L555 118 Z" fill="url(#snowGrad)" />
          <path d="M575 95 L583 88 L590 108 Z" fill="url(#snowGrad)" />
          {/* Kangchenjunga region */}
          <path d="M820 50 L828 42 L835 62 Z" fill="url(#snowGrad)" />
          <path d="M855 45 L864 36 L870 58 Z" fill="url(#snowGrad)" />
          {/* Nun Kun region */}
          <path d="M140 210 L148 204 L155 218 Z" fill="url(#snowGrad)" />
          {/* Gorichen region */}
          <path d="M985 60 L993 53 L1000 72 Z" fill="url(#snowGrad)" />
          {/* Deo Tibba */}
          <path d="M355 158 L362 152 L370 168 Z" fill="url(#snowGrad)" />
          {/* Trishul */}
          <path d="M645 78 L652 72 L660 90 Z" fill="url(#snowGrad)" />

          {/* Layer 3: Foothills — warm accent */}
          <path
            d="M0 320 L0 268 L60 262 L120 270 L180 258 L240 265 L300 252 L360 260 L420 248 L480 255 L540 245 L600 252 L660 242 L720 250 L780 238 L840 246 L900 240 L960 248 L1020 255 L1080 260 L1140 265 L1200 270 L1260 265 L1320 272 L1380 275 L1440 278 L1440 320 Z"
            fill="url(#foothills)"
          />

          {/* State region markers — subtle vertical dashes along the ridge */}
          {[
            { x: 80, label: 'J&K' },
            { x: 170, label: 'Ladakh' },
            { x: 350, label: 'Himachal' },
            { x: 540, label: 'Uttarakhand' },
            { x: 750, label: 'W.Bengal' },
            { x: 840, label: 'Sikkim' },
            { x: 930, label: 'Assam' },
            { x: 1000, label: 'Arunachal' },
            { x: 1050, label: 'Meghalaya' },
            { x: 1100, label: 'Nagaland' },
            { x: 1150, label: 'Manipur' },
            { x: 1210, label: 'Mizoram' },
            { x: 1270, label: 'Tripura' },
          ].map(s => (
            <g key={s.label}>
              <text
                x={s.x} y={278}
                textAnchor="middle"
                fill="rgba(255,255,255,0.22)"
                fontSize="6"
                fontFamily="Georgia, serif"
                letterSpacing="1"
              >
                {s.label}
              </text>
            </g>
          ))}

          {/* Peak labels — major ones */}
          {PEAKS.filter(p => p.major).map(peak => (
            <g key={peak.name}>
              {/* Tiny dot at peak */}
              <circle cx={peak.x} cy={peak.y - 8} r="1.5" fill="rgba(255,255,255,0.5)" />
              {/* Thin line down from dot */}
              <line x1={peak.x} y1={peak.y - 6} x2={peak.x} y2={peak.y + 5} stroke="rgba(255,255,255,0.18)" strokeWidth="0.5" />
              {/* Peak name */}
              <text
                x={peak.x} y={peak.y - 14}
                textAnchor="middle"
                fill="rgba(255,255,255,0.45)"
                fontSize="7.5"
                fontFamily="Georgia, serif"
                letterSpacing="1.2"
                fontStyle="italic"
              >
                {peak.name}
              </text>
              {/* Altitude */}
              <text
                x={peak.x} y={peak.y - 4}
                textAnchor="middle"
                fill="rgba(255,255,255,0.25)"
                fontSize="5.5"
                fontFamily="Georgia, serif"
                letterSpacing="0.5"
              >
                {peak.alt}
              </text>
            </g>
          ))}

          {/* Secondary peak labels — subtler */}
          {PEAKS.filter(p => !p.major).map(peak => (
            <g key={peak.name}>
              <circle cx={peak.x} cy={peak.y - 4} r="1" fill="rgba(255,255,255,0.35)" />
              <text
                x={peak.x} y={peak.y - 10}
                textAnchor="middle"
                fill="rgba(255,255,255,0.3)"
                fontSize="6"
                fontFamily="Georgia, serif"
                letterSpacing="0.8"
                fontStyle="italic"
              >
                {peak.name}
              </text>
            </g>
          ))}

          {/* Scale bar */}
          <line x1="40" y1="305" x2="140" y2="305" stroke="rgba(255,255,255,0.2)" strokeWidth="0.6" />
          <line x1="40" y1="302" x2="40" y2="308" stroke="rgba(255,255,255,0.2)" strokeWidth="0.6" />
          <line x1="140" y1="302" x2="140" y2="308" stroke="rgba(255,255,255,0.2)" strokeWidth="0.6" />
          <text x="90" y="315" textAnchor="middle" fill="rgba(255,255,255,0.18)" fontSize="5.5" fontFamily="Georgia, serif" letterSpacing="1.5">~500 KM</text>
        </svg>

        {/* Footer text */}
        <div className="absolute bottom-0 left-0 right-0 py-5 text-center bg-gradient-to-t from-[#141c24] to-transparent">
          <Link to="/" className="text-white/25 text-xs tracking-wider hover:text-white/40 transition-colors uppercase">
            GoHimalayan &middot; The Himalayan Homestay Index
          </Link>
        </div>
      </div>
    </section>
  );
}
